import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { toast } from 'sonner';
import api from '../../lib/api';
import { 
  TrendingUp, TrendingDown, DollarSign, Users, Calendar, 
  ShoppingCart, RefreshCw, Loader2, BarChart3, PieChart,
  ArrowUpRight, ArrowDownRight, Target, Star
} from 'lucide-react';

export default function AnalyticsDashboardTab() {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/analytics/dashboard');
      setAnalytics(res.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Failed to load analytics data</p>
        <Button onClick={loadAnalytics} className="mt-4">Retry</Button>
      </div>
    );
  }

  // Calculate week-over-week changes
  const revenueData = analytics.revenue_trend || [];
  const last7Days = revenueData.slice(-7);
  const prev7Days = revenueData.slice(-14, -7);
  const last7Revenue = last7Days.reduce((sum, d) => sum + d.revenue, 0);
  const prev7Revenue = prev7Days.reduce((sum, d) => sum + d.revenue, 0);
  const revenueChange = prev7Revenue > 0 ? ((last7Revenue - prev7Revenue) / prev7Revenue * 100).toFixed(1) : 0;

  const bookingsData = analytics.bookings_trend || [];
  const last7Bookings = bookingsData.slice(-7).reduce((sum, d) => sum + d.count, 0);
  const prev7Bookings = bookingsData.slice(-14, -7).reduce((sum, d) => sum + d.count, 0);
  const bookingsChange = prev7Bookings > 0 ? ((last7Bookings - prev7Bookings) / prev7Bookings * 100).toFixed(1) : 0;

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-primary" />
            Analytics Dashboard
          </h2>
          <p className="text-muted-foreground">Track your business performance</p>
        </div>
        <Button onClick={loadAnalytics} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Revenue */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Revenue</p>
              <p className="text-3xl font-bold text-green-600">${analytics.total_revenue?.toLocaleString()}</p>
            </div>
            <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-1">
            {parseFloat(revenueChange) >= 0 ? (
              <ArrowUpRight className="h-4 w-4 text-green-500" />
            ) : (
              <ArrowDownRight className="h-4 w-4 text-red-500" />
            )}
            <span className={`text-sm font-medium ${parseFloat(revenueChange) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {revenueChange}%
            </span>
            <span className="text-sm text-muted-foreground">vs last week</span>
          </div>
        </Card>

        {/* Total Bookings */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Bookings</p>
              <p className="text-3xl font-bold text-blue-600">{analytics.total_bookings}</p>
            </div>
            <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
              <ShoppingCart className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-1">
            {parseFloat(bookingsChange) >= 0 ? (
              <ArrowUpRight className="h-4 w-4 text-green-500" />
            ) : (
              <ArrowDownRight className="h-4 w-4 text-red-500" />
            )}
            <span className={`text-sm font-medium ${parseFloat(bookingsChange) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {bookingsChange}%
            </span>
            <span className="text-sm text-muted-foreground">vs last week</span>
          </div>
        </Card>

        {/* Average Booking Value */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Avg Booking Value</p>
              <p className="text-3xl font-bold text-purple-600">${analytics.avg_booking_value?.toFixed(2)}</p>
            </div>
            <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
              <Target className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </Card>

        {/* Customer Segments */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Customers</p>
              <p className="text-3xl font-bold text-orange-600">
                {(analytics.customer_segments?.new || 0) + (analytics.customer_segments?.returning || 0)}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
              <Users className="h-6 w-6 text-orange-600" />
            </div>
          </div>
          <div className="mt-4 flex gap-4 text-sm">
            <span className="text-muted-foreground">
              <span className="font-medium text-green-600">{analytics.customer_segments?.new || 0}</span> new
            </span>
            <span className="text-muted-foreground">
              <span className="font-medium text-blue-600">{analytics.customer_segments?.returning || 0}</span> returning
            </span>
          </div>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend Chart (Simple Bar) */}
        <Card className="p-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-500" />
            Revenue Trend (Last 30 Days)
          </h3>
          <div className="space-y-2">
            {revenueData.slice(-14).map((day, idx) => {
              const maxRevenue = Math.max(...revenueData.map(d => d.revenue)) || 1;
              const width = (day.revenue / maxRevenue) * 100;
              return (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-16">{day.date.slice(5)}</span>
                  <div className="flex-1 bg-muted rounded-full h-4 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-400 to-green-600 rounded-full transition-all duration-500"
                      style={{ width: `${width}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium w-16 text-right">${day.revenue}</span>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Popular Tours */}
        <Card className="p-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <Star className="h-5 w-5 text-amber-500" />
            Top Performing Tours
          </h3>
          <div className="space-y-3">
            {(analytics.popular_tours || []).slice(0, 5).map((tour, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                    idx === 0 ? 'bg-amber-500 text-white' : 
                    idx === 1 ? 'bg-gray-400 text-white' : 
                    idx === 2 ? 'bg-amber-700 text-white' : 
                    'bg-muted text-muted-foreground'
                  }`}>
                    {idx + 1}
                  </span>
                  <div>
                    <p className="font-medium text-sm">{tour.title}</p>
                    <p className="text-xs text-muted-foreground">{tour.bookings} bookings</p>
                  </div>
                </div>
                <span className="font-semibold text-green-600">${tour.revenue?.toLocaleString()}</span>
              </div>
            ))}
            {(!analytics.popular_tours || analytics.popular_tours.length === 0) && (
              <p className="text-center text-muted-foreground py-4">No tour data available</p>
            )}
          </div>
        </Card>

        {/* Booking Status Distribution */}
        <Card className="p-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <PieChart className="h-5 w-5 text-blue-500" />
            Booking Status Distribution
          </h3>
          <div className="space-y-3">
            {(analytics.status_distribution || []).map((status, idx) => {
              const colors = {
                confirmed: 'bg-green-500',
                pending: 'bg-yellow-500',
                cancelled: 'bg-red-500',
                completed: 'bg-blue-500',
                unknown: 'bg-gray-500'
              };
              const total = analytics.status_distribution.reduce((sum, s) => sum + s.count, 0);
              const percentage = total > 0 ? ((status.count / total) * 100).toFixed(1) : 0;
              return (
                <div key={idx} className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${colors[status.status] || 'bg-gray-400'}`} />
                  <span className="flex-1 capitalize text-sm">{status.status}</span>
                  <span className="text-sm text-muted-foreground">{status.count}</span>
                  <span className="text-sm font-medium w-12 text-right">{percentage}%</span>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Peak Booking Days */}
        <Card className="p-6">
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-purple-500" />
            Peak Booking Days
          </h3>
          <div className="flex items-end justify-between h-40 gap-2">
            {(analytics.peak_booking_days || []).map((day, idx) => {
              const maxBookings = Math.max(...(analytics.peak_booking_days || []).map(d => d.bookings)) || 1;
              const height = (day.bookings / maxBookings) * 100;
              return (
                <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                  <div 
                    className="w-full bg-gradient-to-t from-purple-500 to-purple-300 rounded-t-lg transition-all duration-500"
                    style={{ height: `${height}%`, minHeight: '4px' }}
                  />
                  <span className="text-xs font-medium">{day.day}</span>
                  <span className="text-xs text-muted-foreground">{day.bookings}</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
